// Deep merge utility for partial config overrides
import deepmerge from 'deepmerge';

// Custom array merge strategy for arrays with objects that have 'id' fields
const idArrayMerge = (keys = ["sections", "faqs", "resources", "items", "articles", "hubs"]) => (a, b, opts) => {
  const key = opts.keyPath?.slice(-1)[0];
  if (!keys.includes(key)) return b;
  
  const byId = arr => Object.fromEntries((arr || []).filter(x => x?.id).map(x => [x.id, x]));
  const A = byId(a || []);
  const B = byId(b || []);
  
  return [...new Set([...Object.keys(A), ...Object.keys(B)])]
    .map(id => deepmerge(A[id] || {}, B[id] || {}, opts));
};

// Merge article config with overrides
export const mergeArticleConfig = (base, override) => {
  if (!override) return base;
  
  return deepmerge(base, override, { 
    arrayMerge: idArrayMerge(["sections", "faqs", "resources", "items", "articles"]) 
  });
};

// Merge hub config with overrides
export const mergeHubConfig = (base, override) => {
  if (!override) return base;
  
  return deepmerge(base, override, { 
    arrayMerge: idArrayMerge(["sections", "faqs", "resources", "items", "hubs"]) 
  });
};

// Generic config merger
export const mergeConfig = (base, override, options = {}) => {
  if (!override) return base;
  
  return deepmerge(base, override, {
    arrayMerge: idArrayMerge(options.arrayKeys || ["sections", "faqs", "resources", "items", "articles", "hubs"]),
    ...options
  });
};

// Validate that override has expected structure
export const validateOverride = (override, expectedKeys = ['byId', '__hash']) => {
  if (!override || typeof override !== 'object') return false;
  
  return expectedKeys.every(key => key in override);
};

// Extract relevant fields for hashing (only the fields that get translated)
export const extractRelevantFields = (article) => {
  const { id, title, shortDescription, content, faqs, meta } = article;
  return { id, title, shortDescription, content, faqs, meta };
};

// Generate hash for deterministic builds (browser-compatible)
export const generateContentHash = (content) => {
  // Use a simple hash function for browser compatibility
  const str = JSON.stringify(content);
  let hash = 0;
  if (str.length === 0) return hash.toString();
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(16);
};
